import React from 'react'
import { Box } from '@mui/material'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { ParticipantsContainerMode } from '../ParticipantsContainer/ParticipantsContainer'

export interface TeamParticipant {
  id: string
  name: string
  email: string
}

export interface TeamParticipantCardProps {
  participant: TeamParticipant
  onRemove: (id: string) => void
  showRemoveButton?: boolean
  mode?: ParticipantsContainerMode
  className?: string
}

/**
 * Helper function to generate initials from participant name
 */
const getParticipantInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2)
}

/**
 * TeamParticipantCard component for displaying team participants with constrained email width
 */
export const TeamParticipantCard: React.FC<TeamParticipantCardProps> = ({
  participant,
  onRemove,
  showRemoveButton = true,
  mode,
  className = ''
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: isDraggingFromHook
  } = useDraggable({
    id: participant.id,
    data: {
      type: 'participant',
      participant
    },
    disabled: mode === 'preview' // Disable drag in preview mode
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDraggingFromHook ? 0.5 : 1
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onRemove(participant.id)
  }

  return (
    <Box className={`flex items-center gap-2 ${className}`}>
      {/* Draggable participant card */}
      <Box
        ref={setNodeRef}
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue,
          borderColor: INTERAX_COLORS.heritageBlue,
          // A flex item never shrinks below its nowrap name on its own; without this the card overflows the team card instead of truncating.
          minWidth: 0,
          ...style
        }}
        {...listeners}
        {...attributes}
        className="flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move flex-1"
      >
        {/* Avatar with initials */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: INTERAX_COLORS.sun
          }}
        >
          <Box
            sx={{
              color: INTERAX_COLORS.offBlack,
              fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
            }}
            className="text-sm font-semibold"
          >
            {getParticipantInitials(participant.name)}
          </Box>
        </Box>

        {/* Participant info with constrained email width */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              color: INTERAX_COLORS.white,
              fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body2.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body2.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body2.lineHeight
            }}
            className="font-medium truncate"
            title={participant.name}
          >
            {participant.name}
          </Box>
          <Box
            sx={{
              color: INTERAX_COLORS.thunderGrey,
              fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight,
              maxWidth: '200px'
            }}
            className="text-sm truncate"
            title={participant.email}
          >
            {participant.email}
          </Box>
        </Box>
      </Box>

      {/* Remove button - outside the draggable area */}
      {showRemoveButton && (
        <Box
          component="button"
          onClick={handleRemove}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: INTERAX_COLORS.redErrorBackground,
            border: 'none',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            flexShrink: 0,
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          <XMarkIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.redError }} />
        </Box>
      )}
    </Box>
  )
}
